import { Alert, Card } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Form, FormsRecipientsResponse, NewFormsRecipient, VerifiedEmail } from '@/api';
import { useRustyState } from '../../state';
import { CreateFormsRecipient } from './Create';
import { FormRecipient } from './Recipient';

export interface FormsRecipientDetailProps {
  submitFormCb: (newForm: NewFormsRecipient) => Promise<void>;
  getVerifiedEmails: () => Promise<FormsRecipientsResponse>;
  getFormVerifiedEmails: () => Promise<FormsRecipientsResponse>;
  deleteRecipientCp: (formId: string, recipientId: string) => Promise<void>;
  form: Form;
  onRecipientsChange?: () => Promise<void>;
}

export function FormsRecipientDetail(props: FormsRecipientDetailProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const [verifiedEmails, setVerifiedEmails] = useState<VerifiedEmail[]>([]);

  const submitCb = async (recipient: NewFormsRecipient) => {
    try {
      setIsBusy(true);
      setError('');
      await props.submitFormCb(recipient);
      const data = await props.getFormVerifiedEmails();
      setVerifiedEmails(data.data);
      if (props.onRecipientsChange) {
        await props.onRecipientsChange();
      }
    } catch (e) {
      setError(`Error: ${e}`);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };

  const deleteCb = async (formId: string, recipientId: string) => {
    try {
      setIsBusy(true);
      setError('');
      await props.deleteRecipientCp(formId, recipientId);
      const data = await props.getFormVerifiedEmails();
      setVerifiedEmails(data.data);
      if (props.onRecipientsChange) {
        await props.onRecipientsChange();
      }
    } catch (e) {
      setError(`Error: ${e}`);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    const getVerifiedEmails = async () => {
      setIsBusy(true);
      try {
        const data = await props.getFormVerifiedEmails();
        setVerifiedEmails(data.data);
      } catch (e) {
        setError(`Error: ${e}`);
        console.error(e);
      } finally {
        setIsBusy(false);
      }
    };
    getVerifiedEmails();
  }, []);

  const FormRecipients = () =>
    verifiedEmails.map((verifiedEmail) => (
      <Card key={verifiedEmail.id} shadow="xs" radius="md" mb="md" withBorder>
        <FormRecipient
          key={`verified-email-${verifiedEmail.id}`}
          verifiedEmail={verifiedEmail}
          form_id={props.form.id}
          deleteCb={deleteCb}
          isBusy={isBusy}
        />
      </Card>
    ));

  const FormRecipientsNotFound = () => (
    <Alert color="primary" icon={<IconAlertCircle size={20} />} mb="xs" mt="xs">
      {t('formsRecipient.notSetup')}
    </Alert>
  );

  return (
    <>
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={20} />} mb="xs" mt="xs">
          {error}
        </Alert>
      )}
      {verifiedEmails && verifiedEmails.length > 0 ? (
        <FormRecipients />
      ) : (
        <FormRecipientsNotFound />
      )}
      <CreateFormsRecipient
        submitFormCb={submitCb}
        getVerifiedEmails={useRustyState.getState().getAndSetVerifiedEmails}
        form={props.form}
      />
    </>
  );
}
